/**
 * The knock the shell currently has open, read once.
 *
 * Two surfaces need it — the pane, which shows the whole card, and the desk,
 * which shows the verdict and the two buttons — and they must not be two reads
 * of the same knock. Two reads would be two answers the moment either one acted,
 * and "accepted in the desk, still standing in the pane" is exactly the bug this
 * exists to make impossible.
 *
 * It is not a query key. `fetchInboxKnock` is a per-knock read that the inbox
 * has always done ad-hoc, and caching it under a key would mean invalidating it
 * from five places that currently invalidate the list.
 *
 * WHICH KNOCK: the side the thread wears, when it is one standing at the
 * reader's door (`docs/conversation-model.md` §6.3).
 *
 * TWO STATES ARE RE-READ ON A CLOCK, and they are the two where somebody else
 * is writing into this thread.
 *
 * A HEARING WITH NO READ YET: the recipient is watching a conversation
 * somebody else is having, and the mechanism the assessed view already uses —
 * re-read the knock — is the mechanism here too, until the doorman publishes.
 *
 * A KNOCK THAT WENT THROUGH: the person let in writes back into the same
 * thread (`docs/conversation-model.md` §3), and nothing pushes it here. Until
 * this clock covered `let_in`, their answer sat on the server until the owner
 * clicked away and back — the owner watched exactly that on staging. It stops
 * at `declined` and `blocked`, where nobody is writing.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchInboxKnock,
  type InboxTranscriptLine,
  type RecipientKnockDetail,
} from "../../lib/api";
import { knockRefOf, type Conversation } from "../../lib/conversations";
import { useSessionExpired } from "../../lib/session";

/** How often a live thread is re-read. Short enough to follow a turn; not a poll storm. */
export const LIVE_THREAD_REFRESH_MS = 4_000;

export type ActiveKnock = {
  /** The knock id, when the open conversation is one standing at your door. */
  knockId: string | null;
  knock: RecipientKnockDetail | null;
  transcript: InboxTranscriptLine[] | null;
  failed: boolean;
  /**
   * THE SERVER NO LONGER SERVES THIS KNOCK TO THIS READER (b gap 4, wave 17b).
   *
   * A block takes the conversation out of every list the server answers, and
   * the knock read 404s from the same instant — but the poll below only stops
   * when there is no knock to poll, and a 404 used to leave the last copy
   * standing and merely raise `failed`. So one block produced a 404 every four
   * seconds, for ever, each one red in the console, under a screen that said
   * "Nob is not answering. Try that again." about a knock that was not missing,
   * it was gone.
   *
   * It is its own state and not `failed`: nothing is broken, nothing may be
   * retried, and the row itself leaves the lists the moment the undo window
   * shuts (`use-inbox-actions.ts`).
   */
  gone: boolean;
  reload: () => void;
};

export function useActiveKnock(conv: Conversation | undefined): ActiveKnock {
  const sessionExpired = useSessionExpired();
  const ref = conv ? knockRefOf(conv) : null;
  const knockId = ref && ref.side === "in" ? ref.knockId : null;

  const [knock, setKnock] = useState<RecipientKnockDetail | null>(null);
  const [transcript, setTranscript] = useState<InboxTranscriptLine[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [gone, setGone] = useState(false);
  const [attempt, setAttempt] = useState(0);
  /** Which knock the state on screen belongs to — see the clear below. */
  const shown = useRef<string | null>(null);
  /**
   * The same fact the effect can read WITHOUT re-running on it. `reload` is
   * called from the undo window and from every verb, and a gone knock asked for
   * again is the loop this exists to end.
   */
  const missing = useRef(false);

  useEffect(() => {
    if (!knockId) {
      shown.current = null;
      missing.current = false;
      setKnock(null);
      setTranscript(null);
      setFailed(false);
      setGone(false);
      return;
    }
    let live = true;
    setFailed(false);
    /**
     * CLEARED WHEN THE KNOCK CHANGES, AND ONLY THEN. Showing the previous
     * knock's card under the new knock's name for a frame is worse than showing
     * nothing for one — but a RE-READ of the knock already on screen is a
     * different act, and blanking for it would make every owner turn in the
     * chat window flash the card and the whole transcript away and back
     * (ruling 2026-08-25 #9: the re-read after a turn is the common case now).
     */
    if (shown.current !== knockId) {
      shown.current = knockId;
      missing.current = false;
      setKnock(null);
      setTranscript(null);
      setGone(false);
    }
    // Asked once and answered 404: never asked again for this knock.
    if (missing.current) return;
    void fetchInboxKnock(knockId).then((r) => {
      if (!live) return;
      if (!r.ok) {
        if (r.code === "UNAUTHENTICATED") return sessionExpired();
        if (r.code === "KNOCK_NOT_FOUND") {
          // Clearing the knock is also what stops the clock below: it runs on
          // the two states a knock can be in, and there is no knock now.
          missing.current = true;
          setGone(true);
          setKnock(null);
          setTranscript(null);
          return;
        }
        setFailed(true);
        return;
      }
      setKnock(r.knock);
      setTranscript(r.knock.transcript);
    });
    return () => {
      live = false;
    };
  }, [knockId, attempt, sessionExpired]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  // The clock, on either of the two live states. A guard-closed hearing is not
  // running, so it stops the hearing's half; a door that opened starts the
  // other half and it runs for as long as the thread is on screen.
  const hearing = knock !== null && !knock.assessed && !knock.guard_closed;
  const through = knock !== null && knock.gate === "let_in";
  const live = hearing || through;
  useEffect(() => {
    if (!live) return;
    const timer = setInterval(reload, LIVE_THREAD_REFRESH_MS);
    return () => clearInterval(timer);
  }, [live, reload]);

  return { knockId, knock, transcript, failed, gone, reload };
}
